import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BrainCircuit, LineChart, ShieldCheck, Workflow } from 'lucide-react';
import SEO from '../components/SEO';
import { Section } from '../components/Section';
import { buildFAQSchema, GeoSeoSections } from '../components/GeoSeoSections';

const faqs = [
  {
    question: '¿Qué hace una consultoría IA para empresas en España?',
    answer: 'Define prioridades, automatizaciones y agentes útiles para ventas, reporting, atención y operación interna, aterrizados a procesos reales del negocio.'
  },
  {
    question: '¿Neuralia369 sirve para pymes y grupos empresariales en España?',
    answer: 'Sí. La propuesta encaja tanto en pymes que necesitan orden comercial como en grupos que quieren estandarizar seguimiento, documentación y visibilidad ejecutiva.'
  },
  {
    question: '¿La implantación incluye estrategia y operativa?',
    answer: 'Sí. No nos quedamos en el diagnóstico: dejamos estructura, flujos, criterios de prioridad y una base utilizable por dirección y equipo.'
  },
  {
    question: '¿Qué sectores suelen obtener más valor?',
    answer: 'Servicios profesionales, educación premium, salud privada, despachos, consultoría comercial y empresas con alto volumen de seguimiento y coordinación.'
  }
];

const successCases = [
  {
    title: 'Firma de consultoría con seguimiento comercial disperso',
    copy: 'Centralización de leads, propuestas, recordatorios y reporting para que gerencia vea oportunidades activas sin depender de hojas sueltas.',
    outcome: 'más velocidad de cierre y menos fugas'
  },
  {
    title: 'Grupo de servicios que opera con varios equipos',
    copy: 'Diseño de una capa orquestadora para unificar tareas, estados de clientes, documentos y acciones repetitivas en una sola vista operativa.',
    outcome: 'más claridad ejecutiva y menos fricción interna'
  },
  {
    title: 'Negocio experto que necesita vender mejor su conocimiento',
    copy: 'Sistema para captación, priorización de oportunidades y secuencia de seguimiento con IA sin perder supervisión humana.',
    outcome: 'más continuidad comercial y mejor experiencia del cliente'
  }
];

const sectors = [
  { badge: 'España', title: 'Consultoría y despachos', copy: 'Ideal para ordenar propuestas, seguimiento, documentación y reporting de dirección.' },
  { badge: 'España', title: 'Clínicas y salud privada', copy: 'Encaja en flujos con leads, presupuestos, revisiones y comunicación estructurada.' },
  { badge: 'España', title: 'Educación premium', copy: 'Útil para captación, seguimiento de interesados, onboarding y operaciones académicas.' },
  { badge: 'España', title: 'Servicios B2B', copy: 'Especialmente potente cuando el negocio vive de llamadas, propuestas, renovaciones y coordinación interna.' }
];

const localSignals = [
  'Diseñado para empresas en España que necesitan visibilidad ejecutiva, trazabilidad y mejor control del pipeline comercial.',
  'Útil cuando el equipo ya trabaja con muchas conversaciones, propuestas, documentos y tareas repartidas entre varias personas.',
  'Pensado para integrar criterio humano + automatización práctica, evitando sistemas complejos que luego nadie usa.',
  'Se puede adaptar a consultoría IA, reporting, automatización comercial y sistema multiagente en una misma capa.'
];

const faqSchema = buildFAQSchema(faqs);

export default function ConsultoriaEspanaPage() {
  return (
    <>
      <SEO
        title="Consultoría IA para empresas en España | Neuralia369"
        description="Consultoría IA para empresas en España: automatización, sistema multiagente, reporting y despliegue operativo con Neuralia369."
        path="/consultoria-ia-espana"
        keywords="consultoría IA España, automatización con IA España, agentes IA empresas España, sistema multiagente España"
        schemas={[faqSchema]}
      />
      <div className="min-h-screen bg-background pt-24 pb-20" data-testid="consultoria-espana-page">
        <Section>
          <div className="max-w-6xl mx-auto grid gap-10 xl:grid-cols-[1.05fr_0.95fr]">
            <div>
              <p className="editorial-kicker mb-4">España · Neuralia369</p>
              <h1>Consultoría IA para empresas en <span className="gradient-text">España</span></h1>
              <p className="mt-6 text-lg text-text-body">Diseñamos sistemas de automatización, reporting y agentes IA para empresas que necesitan mejorar operación, seguimiento comercial y claridad ejecutiva.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {['España', 'Consultoría IA', 'Sistema multiagente', 'Automatización comercial'].map((item) => <span key={item} className="insight-chip">{item}</span>)}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contacto" className="btn-primary" data-testid="consultoria-espana-contact-cta">Solicitar propuesta<ArrowRight className="w-5 h-5" /></Link>
                <Link to="/agenda" className="btn-secondary" data-testid="consultoria-espana-agenda-cta">Agendar sesión</Link>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {[
                { icon: BrainCircuit, title: 'Mapa de IA', copy: 'Priorizamos procesos, herramientas y decisiones con mejor retorno.' },
                { icon: Workflow, title: 'Flujos operativos', copy: 'Unificamos ventas, operaciones y seguimiento en una misma lógica.' },
                { icon: LineChart, title: 'Reporting', copy: 'Métricas ejecutivas y visibilidad para decidir con más rapidez.' },
                { icon: ShieldCheck, title: 'Implantación real', copy: 'No solo estrategia: dejamos procesos utilizables por el equipo.' },
              ].map(({ icon: Icon, title, copy }) => (
                <div key={title} className="glass-card tracing-panel p-6" data-testid={`consultoria-espana-card-${title}`}>
                  <Icon className="w-6 h-6 text-primary" />
                  <p className="mt-4 text-lg font-semibold text-text-heading">{title}</p>
                  <p className="mt-3 text-sm text-text-body">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
        <GeoSeoSections
          slug="consultoria-espana"
          regionLabel="España"
          successCases={successCases}
          sectors={sectors}
          faqs={faqs}
          localSignals={localSignals}
        />
      </div>
    </>
  );
}